import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Zap, CheckCircle2, RefreshCw, Power, Wifi, LifeBuoy } from 'lucide-react';
import { EvieOrb } from '@/components/primitives';
import { nudgeCharger } from '@/lib/api';
import { toast } from '@/store/toast';
import styles from './ChargerRecoveryFlow.module.css';

/**
 * EVII-voiced guided recovery for an offline home charger.
 *
 * Opened when a nudge finds no live server-side connection, or when the
 * owner proactively asks for help from the offline My Charger page. EVII
 * walks them back online one step at a time — check power and breaker,
 * power-cycle, check Wi-Fi — then watches for the charger to re-announce
 * itself. The 10s my-chargers poll drives `isOnline`; when a
 * BootNotification lands, the charger flips back to online and this flow
 * celebrates rather than leaving the owner to refresh.
 *
 * The parent (OwnerStartSection) owns the offline/online branch and only
 * mounts this while the charger is offline, so `isOnline` starts false.
 * When it flips true, from any step, we jump straight to the celebration.
 */
export function ChargerRecoveryFlow({
  identity, isOnline, onDone,
}: {
  identity: string;
  isOnline: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation('myChargersManage');
  // steps 0..2 are the guided checks; 'watching' waits for reconnect;
  // 'reconnected' celebrates.
  const [step, setStep] = useState<0 | 1 | 2 | 'watching' | 'reconnected'>(0);
  const [nudging, setNudging] = useState(false);
  const celebratedRef = useRef(false);

  // Reconnect wins from anywhere. The moment the poll reports the charger
  // online, celebrate once — even mid-checklist (a power-cycle at step 2
  // often brings it back before the owner reaches "watching").
  useEffect(() => {
    if (isOnline && !celebratedRef.current) {
      celebratedRef.current = true;
      setStep('reconnected');
      toast(t('recovery.backOnlineToast'), { tone: 'accent' });
    }
  }, [isOnline]);

  const retryNudge = async (): Promise<void> => {
    setNudging(true);
    try {
      await nudgeCharger(identity);
      // Whatever it returns, the poll is the source of truth for the flip;
      // stay in watching and let isOnline decide.
      setStep('watching');
    } catch {
      setStep('watching');
    } finally {
      setNudging(false);
    }
  };

  return (
    <div className={styles.flow}>
      <div className={styles.header}>
        <EvieOrb size={30} />
        <div className={styles.headerText}>
          <p className={styles.headerName}>EVII</p>
          <p className={styles.headerSub}>
            {step === 'reconnected'
              ? t('recovery.headerSubDone')
              : t('recovery.headerSubDefault')}
          </p>
        </div>
      </div>

      {/* Guided checks 1-3 */}
      {step === 0 && (
        <RecoveryStep
          n={1} total={3} icon={<Power size={18} />}
          title={t('recovery.step1.title')}
          body={t('recovery.step1.body')}
          primary={{ label: t('recovery.doneNext'), onClick: () => setStep(1) }}
        />
      )}
      {step === 1 && (
        <RecoveryStep
          n={2} total={3} icon={<RefreshCw size={18} />}
          title={t('recovery.step2.title')}
          body={t('recovery.step2.body')}
          primary={{ label: t('recovery.doneNext'), onClick: () => setStep(2) }}
          secondary={{ label: t('recovery.back'), onClick: () => setStep(0) }}
        />
      )}
      {step === 2 && (
        <RecoveryStep
          n={3} total={3} icon={<Wifi size={18} />}
          title={t('recovery.step3.title')}
          body={t('recovery.step3.body')}
          primary={{ label: t('recovery.watchingNow'), onClick: () => setStep('watching') }}
          secondary={{ label: t('recovery.back'), onClick: () => setStep(1) }}
        />
      )}

      {/* Watching for the charger to re-announce */}
      {step === 'watching' && (
        <div className={styles.watching}>
          <Loader2 size={22} className={styles.spin} />
          <p className={styles.watchingTitle}>{t('recovery.watchingTitle')}</p>
          <p className={styles.watchingBody}>
            {t('recovery.watchingBody')}
          </p>
          <div className={styles.watchingActions}>
            <button
              type="button"
              className={styles.ghostBtn}
              onClick={retryNudge}
              disabled={nudging}
            >
              {nudging ? <Loader2 size={15} className={styles.spin} /> : <RefreshCw size={15} />}
              {t('recovery.nudgeAgain')}
            </button>
            <a className={styles.ghostBtn} href="mailto:support@evinitiative.com">
              <LifeBuoy size={15} /> {t('recovery.getHelp')}
            </a>
          </div>
        </div>
      )}

      {/* Reconnected — celebrate */}
      {step === 'reconnected' && (
        <div className={styles.reconnected}>
          <CheckCircle2 size={26} className={styles.reconnectedIcon} />
          <p className={styles.reconnectedTitle}>{t('recovery.reconnectedTitle')}</p>
          <p className={styles.watchingBody}>
            {t('recovery.reconnectedBody')}
          </p>
          <button type="button" className={styles.primaryBtn} onClick={onDone}>
            <Zap size={16} /> {t('recovery.backToCharging')}
          </button>
        </div>
      )}
    </div>
  );
}

/** A single guided check with EVII's step counter, copy, and actions. */
function RecoveryStep({
  n, total, icon, title, body, primary, secondary,
}: {
  n: number;
  total: number;
  icon: React.ReactNode;
  title: string;
  body: string;
  primary: { label: string; onClick: () => void };
  secondary?: { label: string; onClick: () => void };
}) {
  const { t } = useTranslation('myChargersManage');
  return (
    <div className={styles.step}>
      <div className={styles.stepHead}>
        <span className={styles.stepIcon}>{icon}</span>
        <span className={styles.stepCount}>{t('recovery.stepCounter', { n, total })}</span>
      </div>
      <p className={styles.stepTitle}>{title}</p>
      <p className={styles.stepBody}>{body}</p>
      <div className={styles.stepActions}>
        {secondary && (
          <button type="button" className={styles.ghostBtn} onClick={secondary.onClick}>
            {secondary.label}
          </button>
        )}
        <button type="button" className={styles.primaryBtn} onClick={primary.onClick}>
          {primary.label}
        </button>
      </div>
    </div>
  );
}
